import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Reveal, Eyebrow } from "../components/ui";
import { API_BASE_URL, ENDPOINTS } from "../config/api";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider, appleProvider } from "../config/firebase";
import { loginUser, getUser, authFetch } from "../utils/auth";
import { formatMoney } from "../utils/format";
import { getTierLevel, isSubscriptionActive } from "../utils/subscription";

import airtelMoneyLogo from "../assets/images/payments/airtel_money.png";
import mpambaLogo from "../assets/images/payments/mpamba.png";

const CHANNEL_LOGOS: Record<string, string> = {
  airtel_money: airtelMoneyLogo,
  mpamba: mpambaLogo,
  paychangu_airtel_money: airtelMoneyLogo,
  paychangu_mpamba: mpambaLogo,
};

type Step =
  | "login"
  | "loading"
  | "selectPlan"
  | "selectChannel"
  | "processing"
  | "success"
  | "error";

interface Channel {
  name: string;
  slug: string;
  is_active: boolean;
  icon: string;
}

interface Plan {
  id?: string;
  slug?: string;
  name?: string;
  price?: number | string;
  currency?: string;
  durationDays?: number;
  features?: string[];
  [key: string]: any;
}

export default function PaymentLogin() {
  const location = useLocation();
  const navigate = useNavigate();
  const preselectPlanId = (
    location.state as { preselectPlanId?: string } | null
  )?.preselectPlanId;

  const [step, setStep] = useState<Step>("loading");

  // Social login state
  const [socialError, setSocialError] = useState("");
  const [socialLoading, setSocialLoading] = useState(false);
  const [deletionNotice, setDeletionNotice] = useState("");

  // Payment options
  const [channels, setChannels] = useState<Channel[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadError, setLoadError] = useState("");
  // The user's current subscription (0 = none, 1 = Basic, 2 = Standard, 3 = Premium)
  const [currentLevel, setCurrentLevel] = useState(0);
  const [subActive, setSubActive] = useState(false);

  // Checkout selections
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [phone, setPhone] = useState("");
  const [payEmail, setPayEmail] = useState("");
  const [payError, setPayError] = useState("");

  // Processing / polling
  const [statusMessage, setStatusMessage] = useState(
    "Processing your payment…",
  );
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    const currentUser = getUser();
    const returnTo = new URLSearchParams(location.search).get("returnTo");
    if (currentUser && returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }
    if (currentUser) {
      loadPaymentData();
    } else {
      setStep("login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for logout events while on this page
  useEffect(() => {
    const handleAuthChange = () => {
      const currentUser = getUser();
      if (!currentUser) {
        setStep("login");
        setSocialError("");
        setDeletionNotice("");
      }
    };
    window.addEventListener("authChange", handleAuthChange);
    return () => window.removeEventListener("authChange", handleAuthChange);
  }, []);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  async function handleSocialLogin(provider: "google" | "apple") {
    setSocialError("");
    setDeletionNotice("");
    setSocialLoading(true);

    try {
      const providerInstance =
        provider === "google" ? googleProvider : appleProvider;
      const result = await signInWithPopup(auth, providerInstance);
      const idToken = await result.user.getIdToken();

      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.SOCIAL}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider,
          idToken,
          fcmToken: "",
        }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const msg =
          data?.message ||
          data?.error ||
          `Social login failed (status ${res.status}).`;
        setSocialError(msg);
        setSocialLoading(false);
        return;
      }

      loginUser({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
      });

      if (data.pendingDeletion) {
        setDeletionNotice(
          `Your account is scheduled for deletion${
            data.deletionScheduledAt
              ? " on " + new Date(data.deletionScheduledAt).toLocaleDateString()
              : ""
          }. Log in again before then to cancel it.`,
        );
      }

      const returnTo = new URLSearchParams(location.search).get("returnTo");
      if (returnTo) {
        navigate(returnTo, { replace: true });
        return;
      }

      await loadPaymentData();
    } catch (err: any) {
      console.error("Firebase Auth Error:", err);
      if (err.code === "auth/popup-closed-by-user") {
        setSocialError("Login cancelled.");
      } else {
        setSocialError(
          "Unable to reach the server or authenticate. Please try again later.",
        );
      }
      setSocialLoading(false);
    }
  }

  async function loadPaymentData() {
    setStep("loading");
    setLoadError("");
    try {
      const [channelsRes, plansRes, subRes] = await Promise.all([
        authFetch(`${API_BASE_URL}${ENDPOINTS.PAYMENT_CHANNELS}`, {}, 15000),
        authFetch(`${API_BASE_URL}${ENDPOINTS.SUBSCRIPTION_PLANS}`, {}, 15000),
        // Same endpoint Account.tsx uses. If it fails we treat the user as unsubscribed.
        authFetch(
          `${API_BASE_URL}${ENDPOINTS.SUBSCRIPTION_MY}`,
          {},
          15000,
        ).catch(() => null),
      ]);

      if (!channelsRes.ok || !plansRes.ok) {
        throw new Error("Failed to load payment options");
      }

      const channelsJson = await channelsRes.json();
      const plansJson = await plansRes.json();

      const channelList: Channel[] = (
        Object.values(channelsJson?.data || {}) as Channel[]
      ).filter((c) => c?.is_active && /mpamba|airtel/i.test(c.slug));

      const planList: Plan[] = Array.isArray(plansJson) ? plansJson : [];

      // Work out the user's current tier from their subscription
      let level = 0;
      let active = false;
      if (subRes && subRes.ok) {
        const subJson = await subRes.json().catch(() => null);
        const sub = subJson?.data ?? subJson; // unwrap { data: ... } if present
        const planName =
          sub?.plan?.name ||
          sub?.planName ||
          (typeof sub?.plan === "string" ? sub.plan : "");
        level = getTierLevel(planName); // "Standard" -> 2
        // Reuse the same active/expired check the Pricing page uses
        active = isSubscriptionActive({
          subscription: {
            tier: planName,
            status: sub?.status,
            expiresAt: sub?.expiresAt || sub?.endDate,
          },
        });
      }
      setCurrentLevel(level);
      setSubActive(active);

      setChannels(channelList);
      setPlans(planList);

      if (preselectPlanId) {
        const match = planList.find((p) => p.id === preselectPlanId);
        if (match && !(active && planLevelOf(match) <= level)) {
          setSelectedPlan(match);
          setStep("selectChannel");
          return;
        }
      }

      setStep("selectPlan");
    } catch (err) {
      setLoadError("Could not load payment options. Please try again.");
      setStep("selectPlan");
    } finally {
      setSocialLoading(false);
    }
  }

  // A plan's tier level. Checks id, slug and name because the backend shape may vary.
  function planLevelOf(plan: Plan) {
    return Math.max(
      getTierLevel(plan.id),
      getTierLevel(plan.slug),
      getTierLevel(plan.name),
    );
  }

  // Active subscription + this plan is the same tier as the user's current one
  function isCurrentPlan(plan: Plan) {
    return subActive && planLevelOf(plan) === currentLevel;
  }

  // Active subscription + this plan is a lower tier than the user's current one
  function isDowngradePlan(plan: Plan) {
    return subActive && planLevelOf(plan) < currentLevel;
  }

  function requiresPhone(channel: Channel) {
    return /mpamba|airtel|ussd/i.test(channel.slug);
  }

  function requiresEmail(channel: Channel) {
    return /card|bank/i.test(channel.slug);
  }

  function mobileMoneyOperatorFor(channel: Channel) {
    if (/mpamba/i.test(channel.slug)) return "tnm";
    if (/airtel/i.test(channel.slug)) return "airtel";
    return undefined;
  }

  function phonePrefixFor(channel: Channel) {
    if (channel.slug.includes("mpamba")) return "2658";
    if (channel.slug.includes("airtel")) return "2659";
    return "265";
  }

  function canSubmitCheckout() {
    if (!selectedPlan || !selectedChannel) return false;
    if (
      requiresPhone(selectedChannel) &&
      phone.length !== phonePrefixFor(selectedChannel).length + 8
    ) {
      return false;
    }
    if (requiresEmail(selectedChannel) && !payEmail) return false;
    return true;
  }

  async function handleCheckout(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedPlan || !selectedChannel) return;

    setPayError("");
    setStep("processing");
    setStatusMessage("Starting checkout…");

    try {
      const body: Record<string, any> = {
        plan: selectedPlan.id || selectedPlan.slug || selectedPlan.name,
        channel: selectedChannel.slug,
        currency: selectedPlan.currency || "MWK",
      };

      if (requiresPhone(selectedChannel)) {
        body.msisdn = phone;
        body.mobileMoneyOperator = mobileMoneyOperatorFor(selectedChannel);
      }
      if (requiresEmail(selectedChannel)) {
        body.email = payEmail;
      }

      const res = await authFetch(
        `${API_BASE_URL}${ENDPOINTS.SUBSCRIPTION_CHECKOUT}`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        15000,
      );

      const json = await res.json().catch(() => null);

      if (!res.ok) {
        const backendMessage =
          json?.message ||
          (Array.isArray(json?.message) ? json.message.join(", ") : null) ||
          json?.error ||
          `Checkout failed (status ${res.status}).`;
        console.error("Checkout 400/error response:", json);
        setPayError(backendMessage);
        setStep("selectChannel");
        return;
      }

      const reference = json?.data?.reference || json?.reference;

      if (!reference) {
        setPayError(
          "Checkout succeeded but no payment reference was returned.",
        );
        setStep("selectChannel");
        return;
      }

      setStatusMessage("Waiting for payment confirmation…");
      pollVerify(reference);
    } catch (err) {
      setPayError("Could not reach the server. Please try again.");
      setStep("selectChannel");
    }
  }

  function pollVerify(reference: string) {
    attemptsRef.current = 0;
    const MAX_ATTEMPTS = 30;
    const INTERVAL_MS = 4000;

    pollRef.current = setInterval(async () => {
      attemptsRef.current += 1;

      try {
        const res = await authFetch(
          `${API_BASE_URL}${ENDPOINTS.SUBSCRIPTION_VERIFY}`,
          {
            method: "POST",
            body: JSON.stringify({ reference }),
          },
          15000,
        );

        const json = await res.json();
        const paymentStatus = json?.data?.status || json?.status;

        if (paymentStatus === "COMPLETED") {
          if (pollRef.current) clearInterval(pollRef.current);
          setStep("success");
        } else if (paymentStatus === "FAILED") {
          if (pollRef.current) clearInterval(pollRef.current);
          setPayError("Payment failed or was cancelled.");
          setStep("error");
        } else if (attemptsRef.current >= MAX_ATTEMPTS) {
          if (pollRef.current) clearInterval(pollRef.current);
          setPayError(
            "Payment timed out. If you completed it, check again shortly.",
          );
          setStep("error");
        }
      } catch (err) {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          if (pollRef.current) clearInterval(pollRef.current);
          setPayError("Could not confirm payment status.");
          setStep("error");
        }
      }
    }, INTERVAL_MS);
  }

  return (
    <main className="bg-white px-[5%] pt-[88px] md:pt-[140px] pb-16 lg:pb-[100px]">
      <div className="max-w-[1100px] mx-auto">
        <Reveal>
          <Eyebrow>Account</Eyebrow>
        </Reveal>

        {step === "login" && (
          <div className="max-w-[480px] mx-auto">
            <Reveal delay={100}>
              <h1 className="text-[clamp(26px,4vw,38px)] font-bold tracking-[-1px] mb-3">
                Log in to subscribe
              </h1>
            </Reveal>
            <Reveal delay={150}>
              <p className="text-[15px] text-muted leading-relaxed mb-8">
                Sign in with Google or Apple to choose a plan and complete
                payment.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <div className="space-y-5">
                <button
                  type="button"
                  onClick={() => handleSocialLogin("google")}
                  disabled={socialLoading}
                  className="w-full flex items-center justify-center gap-3 bg-white border border-black/10 text-black text-sm font-semibold py-3.5 rounded-full hover:bg-neutral-50 transition-colors duration-200 disabled:opacity-40"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continue with Google
                </button>

                <button
                  type="button"
                  onClick={() => handleSocialLogin("apple")}
                  disabled={socialLoading}
                  className="w-full flex items-center justify-center gap-3 bg-black text-white text-sm font-semibold py-3.5 rounded-full hover:bg-neutral-800 transition-colors duration-200 disabled:opacity-40"
                >
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M16.365 21.5c-1.41 0-2.82-1.01-3.74-1.01-.94 0-2.22.96-3.54.96-1.81 0-3.5-1.12-4.63-3.06-2.3-3.98-.58-9.82 3.15-9.82 1.32 0 2.43.88 3.28.88.83 0 2.1-.97 3.62-.97 1.14 0 3.04.48 3.96 1.87-1.88 1.15-2.25 3.4-2.25 4.57 0 2.09 1.33 3.86 3.15 4.74-.6 1.5-1.56 2.84-2.72 2.84h-.28zm-2.09-18.22c-.64 0-1.4.42-1.9.98-.44.5-.8 1.22-.8 1.96 0 .82.38 1.55.85 2.03.54.55 1.3.9 1.92.9.68 0 1.42-.42 1.9-.98.44-.52.8-1.24.8-2.02 0-.83-.38-1.55-.85-2.03-.54-.55-1.3-.9-1.92-.9z" />
                  </svg>
                  Continue with Apple
                </button>

                {socialError && (
                  <p className="text-[13px] text-rose-dark">{socialError}</p>
                )}
                {socialLoading && (
                  <p className="text-[13px] text-muted">Signing you in…</p>
                )}
              </div>
            </Reveal>
          </div>
        )}

        {step === "loading" && (
          <div className="text-center py-16 max-w-[480px] mx-auto">
            <div className="w-10 h-10 border-2 border-black/10 border-t-rose rounded-full animate-spin mx-auto mb-6" />
            <p className="text-[15px] text-muted">Loading payment options…</p>
          </div>
        )}

        {step === "selectPlan" && (
          <>
            {(() => {
              const u = getUser();
              if (!u) return null;
              return (
                <Reveal delay={50}>
                  <div className="mb-6 md:mb-8 p-4 md:p-6 rounded-2xl bg-neutral-50 border border-black/5 flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-rose text-white font-bold text-base md:text-lg flex items-center justify-center flex-shrink-0">
                      {(u.name?.[0] || u.email?.[0] || "U").toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-0.5">
                        Signed in as
                      </p>
                      <p className="text-[15px] md:text-lg font-bold text-black truncate">
                        {u.name || "User"}
                      </p>
                      <p className="text-[12px] md:text-[13px] text-muted truncate">
                        {u.email || u.phone || ""}
                        {u.phoneVerified && (
                          <span className="ml-2 text-[11px] text-green-600 font-medium">
                            ✓ Verified
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })()}

            <Reveal delay={100}>
              <h1 className="text-[clamp(22px,4vw,38px)] font-bold tracking-[-1px] mb-1 md:mb-2">
                Choose a plan
              </h1>
              <p className="text-[13px] md:text-[15px] text-muted leading-relaxed mb-5 md:mb-8">
                Pick the plan that fits how you want to connect.
              </p>
            </Reveal>

            {deletionNotice && (
              <Reveal delay={125}>
                <div className="mb-6 p-4 rounded-2xl bg-blush border border-rose/20">
                  <p className="text-[13px] text-rose-dark leading-relaxed">
                    {deletionNotice}
                  </p>
                </div>
              </Reveal>
            )}

            {loadError && (
              <div className="mb-6">
                <p className="text-[13px] text-rose-dark mb-3">{loadError}</p>
                <button
                  type="button"
                  onClick={loadPaymentData}
                  className="text-sm font-semibold text-rose hover:underline"
                >
                  Try loading again
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
              {plans.map((plan, i) => (
                <button
                  key={plan.id || plan.slug || i}
                  type="button"
                  disabled={isCurrentPlan(plan) || isDowngradePlan(plan)}
                  onClick={() => {
                    setSelectedPlan(plan);
                    setStep("selectChannel");
                  }}
                  className={`group relative overflow-hidden flex flex-col text-left p-4 md:p-6 rounded-2xl border border-black/10 bg-white transition-all duration-200 h-full ${
                    isCurrentPlan(plan) || isDowngradePlan(plan)
                      ? "opacity-50 grayscale cursor-not-allowed"
                      : "hover:border-rose hover:shadow-lg hover:-translate-y-0.5"
                  }`}
                >
                  {isDowngradePlan(plan) && (
                    <svg
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                    >
                      <line
                        x1="100"
                        y1="0"
                        x2="0"
                        y2="100"
                        stroke="#ef4444"
                        strokeWidth="2"
                        vectorEffect="non-scaling-stroke"
                      />
                    </svg>
                  )}
                  <div className="font-bold text-base md:text-lg mb-1 md:mb-2 text-black">
                    {plan.name || plan.slug}
                  </div>
                  {plan.price !== undefined && (
                    <div className="mb-3 md:mb-4">
                      <span
                        className={`text-xl md:text-2xl font-bold text-black ${
                          isDowngradePlan(plan)
                            ? "line-through decoration-red-500 decoration-2"
                            : ""
                        }`}
                      >
                        {formatMoney(plan.price, plan.currency || "MWK")}
                      </span>
                      {plan.durationDays && (
                        <span className="text-[12px] md:text-[13px] text-muted ml-2">
                          / {plan.durationDays} days
                        </span>
                      )}
                    </div>
                  )}
                  {Array.isArray(plan.features) && plan.features.length > 0 && (
                    <ul className="text-[12px] md:text-[13px] text-muted space-y-1.5 md:space-y-2 mb-4 md:mb-6 flex-1">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <svg
                            className="w-4 h-4 text-rose flex-shrink-0 mt-0.5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <polyline points="20,6 9,17 4,12" />
                          </svg>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-auto pt-3 md:pt-4 border-t border-black/5">
                    <span className="block text-center md:text-left bg-rose/10 md:bg-transparent text-[13px] md:text-sm font-semibold text-rose group-hover:text-rose-dark py-2.5 md:py-0 rounded-full">
                      {isCurrentPlan(plan)
                        ? "Current Plan"
                        : isDowngradePlan(plan)
                          ? "Downgrade unavailable"
                          : subActive
                            ? "Upgrade →"
                            : "Select plan →"}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {step === "selectChannel" && selectedPlan && (
          <div className="max-w-[480px] mx-auto">
            <button
              type="button"
              onClick={() => setStep("selectPlan")}
              className="text-sm text-muted hover:text-black mb-4 flex items-center gap-1"
            >
              ← Change plan
            </button>

            <Reveal delay={100}>
              <h1 className="text-[clamp(26px,4vw,38px)] font-bold tracking-[-1px] mb-2">
                Pay for {selectedPlan.name}
              </h1>
            </Reveal>
            <Reveal delay={150}>
              <p className="text-[15px] text-muted leading-relaxed mb-8">
                {formatMoney(
                  selectedPlan.price,
                  selectedPlan.currency || "MWK",
                )}
                {selectedPlan.durationDays
                  ? ` for ${selectedPlan.durationDays} days`
                  : ""}
              </p>
            </Reveal>

            {channels.length === 0 && (
              <p className="text-[13px] text-rose-dark mb-6">
                No payment methods are currently available.
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {channels.map((channel) => (
                <button
                  key={channel.slug}
                  type="button"
                  onClick={() => {
                    setSelectedChannel(channel);
                    setPhone(phonePrefixFor(channel));
                    setPayError("");
                  }}
                  className={`w-full flex items-center gap-3 text-left p-4 rounded-2xl border transition-all duration-200 ${
                    selectedChannel === channel
                      ? "border-rose bg-blush"
                      : "border-black/10 hover:border-black/20"
                  }`}
                >
                  <img
                    src={CHANNEL_LOGOS[channel.slug] || channel.icon}
                    alt={channel.name}
                    className="w-6 h-6 object-contain flex-shrink-0"
                  />
                  <span className="font-semibold text-sm">
                    {channel.name.replace(/\s*\(PayChangu\)/i, "")}
                  </span>
                </button>
              ))}
            </div>

            {selectedChannel && (
              <Reveal>
                <form onSubmit={handleCheckout} className="space-y-5">
                  {requiresPhone(selectedChannel) && (
                    <div>
                      <label className="block text-[13px] font-semibold text-black mb-2">
                        Mobile number
                      </label>
                      <input
                        type="tel"
                        required
                        value={phone}
                        onChange={(e) => {
                          const prefix = phonePrefixFor(selectedChannel);
                          const digitsOnly = e.target.value.replace(/\D/g, "");
                          const rest = digitsOnly.startsWith(prefix)
                            ? digitsOnly.slice(prefix.length)
                            : digitsOnly.startsWith("265")
                              ? digitsOnly.slice(3)
                              : digitsOnly;
                          setPhone(prefix + rest.slice(0, 8));
                        }}
                        placeholder={`e.g. ${phonePrefixFor(selectedChannel)}12345678`}
                        className="w-full px-4 py-3.5 rounded-2xl border border-black/10 text-sm outline-none focus:border-rose transition-colors duration-200"
                      />
                    </div>
                  )}

                  {requiresEmail(selectedChannel) && (
                    <div>
                      <label className="block text-[13px] font-semibold text-black mb-2">
                        Email for receipt
                      </label>
                      <input
                        type="email"
                        required
                        value={payEmail}
                        onChange={(e) => setPayEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full px-4 py-3.5 rounded-2xl border border-black/10 text-sm outline-none focus:border-rose transition-colors duration-200"
                      />
                    </div>
                  )}

                  {payError && (
                    <p className="text-[13px] text-rose-dark">{payError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={!canSubmitCheckout()}
                    className="w-full bg-magenta text-white text-sm font-semibold py-3.5 rounded-full hover:opacity-90 transition-opacity duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Confirm payment
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        )}

        {step === "processing" && (
          <div className="text-center py-16 max-w-[480px] mx-auto">
            <div className="w-10 h-10 border-2 border-black/10 border-t-rose rounded-full animate-spin mx-auto mb-6" />
            <p className="text-[15px] text-muted">{statusMessage}</p>
          </div>
        )}

        {/* PREMIUM SUCCESS SCREEN */}
        {step === "success" && (
          <Reveal>
            <div className="max-w-[520px] mx-auto py-10 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#16a34a"
                  strokeWidth="3"
                  className="w-8 h-8"
                >
                  <polyline points="20,6 9,17 4,12" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold tracking-tight mb-2">
                Payment Successful
              </h1>
              <p className="text-[15px] text-muted mb-8">
                Thank you for subscribing to Max & Sherry ConnectPlus.
              </p>

              {/* Receipt Card */}
              <div className="bg-neutral-50 border border-black/5 rounded-2xl p-6 text-left mb-8 shadow-sm">
                <div className="flex justify-between items-center pb-4 border-b border-black/5 mb-4">
                  <span className="text-[13px] font-semibold text-muted uppercase tracking-wider">
                    Amount Paid
                  </span>
                  <span className="text-xl font-bold text-black">
                    {formatMoney(
                      selectedPlan?.price || 0,
                      selectedPlan?.currency || "MWK",
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-black/5 mb-4">
                  <span className="text-[13px] font-semibold text-muted uppercase tracking-wider">
                    Plan
                  </span>
                  <span className="text-sm font-bold text-magenta px-3 py-1 bg-blush rounded-full border border-rose/20">
                    {selectedPlan?.name} Tier
                  </span>
                </div>
                <div>
                  <span className="text-[13px] font-semibold text-muted uppercase tracking-wider block mb-3">
                    What's included:
                  </span>
                  <ul className="space-y-2">
                    {selectedPlan?.features?.map((f: string) => (
                      <li
                        key={f}
                        className="flex items-center gap-2 text-sm text-slate-700"
                      >
                        <svg
                          className="w-4 h-4 text-green-600 flex-shrink-0"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                        >
                          <polyline points="20,6 9,17 4,12" />
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <p className="text-[14px] text-muted mb-6">
                Your account has been upgraded. Open the mobile app to start
                matching with your new features!
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={process.env.REACT_APP_APP_DOWNLOAD_URL || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black text-white text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-neutral-800 transition-colors"
                >
                  Open Mobile App
                </a>
                <Link
                  to="/account"
                  className="bg-white text-black border border-black/20 text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-neutral-50 transition-colors"
                >
                  Go to My Account
                </Link>
              </div>
            </div>
          </Reveal>
        )}

        {step === "error" && (
          <Reveal>
            <div className="text-center py-10 max-w-[480px] mx-auto">
              <p className="text-[15px] text-rose-dark leading-relaxed mb-6">
                {payError}
              </p>
              <button
                type="button"
                onClick={() => setStep("selectChannel")}
                className="bg-black text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-neutral-800 transition-colors duration-200"
              >
                Try again
              </button>
            </div>
          </Reveal>
        )}
      </div>
    </main>
  );
}
